import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useScreenshotProtection } from './hooks/useScreenshotProtection';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import CreateOfferPage from './pages/CreateOfferPage';
import OfferViewPage from './pages/OfferViewPage';
import SignedDocumentPage from './pages/SignedDocumentPage';
import AuditPage from './pages/AuditPage';
import CandidatesPage from './pages/CandidatesPage';
import UsersPage from './pages/UsersPage';
import SettingsPage from './pages/SettingsPage';
import Layout from './components/Layout';

// Pages that don't need protection (no sensitive data)
const UNPROTECTED_PATHS = ['/login', '/register', '/forgot-password', '/dashboard', '/candidates', '/offers/create', '/users', '/audit', '/settings'];

function PrivateRoute({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Global screenshot protection — active on all pages except login/register
// SignedDocumentPage handles its own logic (allows when joined)
function GlobalProtection({ children }) {
  const location = useLocation();
  const { isBlurred, warningMessage } = useScreenshotProtection();

  // Don't show blur overlay on unprotected pages
  const isUnprotected = UNPROTECTED_PATHS.includes(location.pathname);
  // SignedDocumentPage manages its own protection
  const isSignedDoc = location.pathname.startsWith('/signed/');

  if (!isUnprotected && !isSignedDoc && isBlurred) {
    return (
      <>
        {children}
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(0,0,0,0.95)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', color: '#fff',
        }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🚫</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
            {warningMessage || 'Content Hidden'}
          </h2>
          <p style={{ color: '#aaa', fontSize: 14 }}>
            Screenshots and printing are not permitted on this platform.
          </p>
        </div>
      </>
    );
  }

  return children;
}

export default function App() {
  return (
    <GlobalProtection>
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      {/* Public candidate route — no auth needed */}
      <Route path="/offer/view/:token" element={<OfferViewPage />} />

      {/* Permanent signed document portal — no auth, no expiry */}
      <Route path="/signed/:signatureId" element={<SignedDocumentPage />} />

      {/* Protected routes */}
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />

        <Route path="dashboard" element={<DashboardPage />} />

        <Route path="candidates" element={
          <PrivateRoute roles={['RECRUITER', 'TENANT_ADMIN', 'SUPER_ADMIN']}>
            <CandidatesPage />
          </PrivateRoute>
        } />

        <Route path="offers/create" element={
          <PrivateRoute roles={['RECRUITER', 'TENANT_ADMIN', 'SUPER_ADMIN']}>
            <CreateOfferPage />
          </PrivateRoute>
        } />

        <Route path="users" element={
          <PrivateRoute roles={['TENANT_ADMIN', 'SUPER_ADMIN']}>
            <UsersPage />
          </PrivateRoute>
        } />

        <Route path="audit" element={
          <PrivateRoute roles={['TENANT_ADMIN', 'SUPER_ADMIN']}>
            <AuditPage />
          </PrivateRoute>
        } />

        <Route path="settings" element={
          <PrivateRoute roles={['TENANT_ADMIN', 'SUPER_ADMIN']}>
            <SettingsPage />
          </PrivateRoute>
        } />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </GlobalProtection>
  );
}
